import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import { FALLBACK } from "@/data";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Shop from "@/components/Shop";
import Manifesto from "@/components/Manifesto";
import Reviews from "@/components/Reviews";
import Faq from "@/components/Faq";
import { CartDrawer, CheckoutModal, SuccessModal } from "@/components/Overlays";
import Assistant from "@/components/Assistant";
import PaymentResult from "@/components/PaymentResult";
import { AuthCallback, AccountModal } from "@/components/Auth";
import AdminPage from "@/components/admin/AdminPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const apiError = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export default function App() {
  const [authHash] = useState(() => window.location.hash);
  const isAuthCallback = authHash.includes("session_id=");
  const [user, setUser] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [product, setProduct] = useState(FALLBACK);
  const [selected, setSelected] = useState("home-pro");
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [success, setSuccess] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeImage, setActiveImage] = useState(null);
  const [qty, setQty] = useState(1);
  const [stock, setStock] = useState({});
  const lenisRef = useRef(null);

  const variant = product.variants.find((v) => v.id === selected) || product.variants[0];
  const variantStock = stock[variant.id] ?? 99;
  const total = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
  const count = cart.reduce((s, i) => s + i.quantity, 0);

  useEffect(() => {
    if (!isAuthCallback) axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => setUser(r.data)).catch(() => {});
  }, [isAuthCallback]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const target = document.querySelector(a.getAttribute("href"));
      if (target) { e.preventDefault(); lenis.scrollTo(target, { offset: -90 }); }
    };
    document.addEventListener("click", onClick);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); document.removeEventListener("click", onClick); };
  }, []);

  useEffect(() => {
    axios.get(`${API}/product`).then((r) => setProduct({ ...FALLBACK, ...r.data, variants: FALLBACK.variants })).catch(() => {});
    axios.get(`${API}/stock`).then((r) => setStock(r.data)).catch(() => {});
  }, []);

  const scrollTo = (sel) => {
    const el = document.querySelector(sel);
    if (el) lenisRef.current?.scrollTo(el, { offset: -90 });
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
    setUser(null);
    setAccountOpen(false);
    toast.success("Vous êtes déconnectée");
  };

  const addItem = () => setCart((items) => {
    const ex = items.find((i) => i.id === variant.id);
    return ex ? items.map((i) => i.id === variant.id ? { ...i, quantity: i.quantity + qty } : i) : [...items, { ...variant, quantity: qty }];
  });
  const inCart = cart.find((i) => i.id === variant.id)?.quantity || 0;
  const canAdd = () => {
    if (variantStock <= 0) { toast.error("Ce coloris est en rupture de stock"); return false; }
    if (inCart + qty > variantStock) { toast.error(`Plus que ${variantStock} exemplaire${variantStock > 1 ? "s" : ""} disponible${variantStock > 1 ? "s" : ""}`); return false; }
    return true;
  };
  const addToCart = () => { if (!canAdd()) return; addItem(); toast.success("Article ajouté au panier"); setCartOpen(true); };
  const buyNow = () => { if (!canAdd()) return; addItem(); setCheckoutOpen(true); };
  const changeQty = (id, d) => setCart((items) => items.map((i) => i.id === id ? { ...i, quantity: i.quantity + d } : i).filter((i) => i.quantity > 0));

  const orderPayload = (form) => ({
    customer_name: form.get("name"), email: form.get("email"), phone: form.get("phone"),
    address: form.get("address"), city: form.get("city"), postal_code: form.get("postal"),
  });

  const submitOrder = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload = {
      ...orderPayload(form),
      items: cart.map((i) => ({ variant_id: i.id, variant_name: i.name, quantity: i.quantity, price: i.price })),
      total, payment_method: "request",
    };
    try { const r = await axios.post(`${API}/orders`, payload); setSuccess(r.data); }
    catch (err) { toast.error(apiError(err, "Impossible d'enregistrer la commande")); return; }
    setCart([]); setCartOpen(false); setCheckoutOpen(false);
    axios.get(`${API}/stock`).then((r) => setStock(r.data)).catch(() => {});
  };

  const payByCard = async (form) => {
    const payload = {
      ...orderPayload(form),
      items: cart.map((i) => ({ variant_id: i.id, quantity: i.quantity })),
      origin_url: window.location.origin,
    };
    try {
      const r = await axios.post(`${API}/payments/checkout`, payload);
      window.location.href = r.data.checkout_url;
    } catch (err) { toast.error(apiError(err, "Le paiement par carte n'est pas disponible pour le moment")); }
  };

  if (isAuthCallback) return <AuthCallback />;
  const path = window.location.pathname;
  if (path.startsWith("/admin")) return <AdminPage />;
  if (path.startsWith("/payment/success")) return <PaymentResult success />;
  if (path.startsWith("/payment/cancel")) return <PaymentResult success={false} />;

  return (
    <div className="vf-site">
      <Toaster position="bottom-right" />
      <Header count={count} onCart={() => setCartOpen(true)} search={search} setSearch={setSearch} menuOpen={menuOpen} setMenuOpen={setMenuOpen} user={user} onAccount={() => (user ? setAccountOpen(true) : login())} />
      <main>
        <Hero onDiscover={() => scrollTo("#product")} />
        <Marquee />
        <Shop product={product} variant={variant} stock={stock} selected={selected} setSelected={setSelected} activeImage={activeImage} setActiveImage={setActiveImage} qty={qty} setQty={setQty} onAdd={addToCart} onBuy={buyNow} />
        <Manifesto />
        <Reviews product={product} />
        <Faq />
      </main>
      <footer className="vf-footer">
        <div className="vf-footer-inner">
          <a className="vf-footer-logo" href="#top" data-testid="footer-logo-link">Vibe<em>Fit</em></a>
          <p>Le mouvement qui prend soin de vous, à la maison.</p>
          <div className="vf-footer-bar">
            <span>Paiement sécurisé · Service client français · Livraison suivie</span>
            <span>© 2026 VibeFit · <a href="/admin" className="vf-footer-admin" data-testid="footer-admin-link">Espace admin</a></span>
          </div>
        </div>
      </footer>
      <CartDrawer open={cartOpen} cart={cart} total={total} onClose={() => setCartOpen(false)} onQty={changeQty} onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); }} />
      <CheckoutModal open={checkoutOpen} total={total} onClose={() => setCheckoutOpen(false)} onSubmit={submitOrder} onPayCard={payByCard} />
      <SuccessModal order={success} onClose={() => setSuccess(null)} />
      <AccountModal open={accountOpen} user={user} onClose={() => setAccountOpen(false)} onLogout={logout} />
      <Assistant />
    </div>
  );
}
